/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { BadgeCheck, Loader2, Search } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import PendingVerificationCard from '@/components/admin/PendingVerificationCard';
import EvidenceZoomDialog from '@/components/admin/EvidenceZoomDialog';
import ReviewConfirmDialog from '@/components/admin/ReviewConfirmDialog';
import { useReviewQueue } from '@/components/admin/useReviewQueue';

/**
 * Admin panel for manual review of pending registry evidence
 * (Albo FNOMCeO / OPI / OSS qualification screenshots).
 *
 * Refactor notes:
 *  - Phase 8: card / evidence-zoom / confirm dialogs live in `components/admin/*`.
 *  - Code Quality: data fetching, filtering and dialog lifecycle moved to
 *    `useReviewQueue`; this file is now a pure renderer.
 */

const QueueHeader = ({ count, filter, onFilterChange }) => (
  <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
    <div>
      <div className="flex items-center gap-2">
        <BadgeCheck className="h-5 w-5 text-primary" />
        <h2 className="font-display font-semibold text-lg">Registry verification queue</h2>
        <Badge variant="secondary" data-testid="admin-onboarding-count">{count} pending</Badge>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Review professional registration evidence (Albo, OPI, OSS) submitted by healthcare professionals.
      </p>
    </div>
    <div className="relative w-full md:w-72">
      <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
      <Input
        placeholder="Filter by name, email, registry number…"
        value={filter}
        onChange={(e) => onFilterChange(e.target.value)}
        className="pl-9"
        data-testid="admin-onboarding-search"
      />
    </div>
  </div>
);

const QueueEmpty = () => (
  <Card className="p-8 text-center bg-secondary/30" data-testid="admin-onboarding-empty">
    <BadgeCheck className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
    <div className="font-semibold">All caught up</div>
    <div className="text-sm text-muted-foreground">No pending registry verifications.</div>
  </Card>
);

const AdminOnboardingPanel = () => {
  const q = useReviewQueue();

  return (
    <div data-testid="admin-onboarding-panel">
      <QueueHeader count={q.items.length} filter={q.filter} onFilterChange={q.setFilter} />

      {q.loading && (
        <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {!q.loading && q.filtered.length === 0 && <QueueEmpty />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {q.filtered.map((u) => (
          <PendingVerificationCard
            key={u.id}
            user={u}
            onOpenEvidence={q.openEvidence}
            onApprove={q.openApprove}
            onReject={q.openReject}
          />
        ))}
      </div>

      <EvidenceZoomDialog
        open={q.imgZoom}
        user={q.selected}
        onClose={q.closeAllDialogs}
        onQuickApprove={() => q.quickAction('approve')}
        onQuickReject={() => q.quickAction('reject')}
      />

      <ReviewConfirmDialog
        open={!!q.selected && !!q.action && !q.imgZoom}
        action={q.action}
        user={q.selected}
        reason={q.reason}
        onReasonChange={q.setReason}
        onCancel={q.closeAllDialogs}
        onConfirm={q.submitReview}
        busy={q.busy}
      />
    </div>
  );
};

export default AdminOnboardingPanel;
